import { useState, useRef, useEffect, useCallback } from "react";
import { useGetScheduledPostV2 } from "../../api/socialpost-api";
import DateSection from "./DateSection";
import DateTab from "./DateTab";
import { addDays, format } from "date-fns";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/popover.tsx'
import { Calendar } from "@/shared/ui/calendar.tsx";
import { Button } from "@/shared/ui/button.tsx";
import { filterScheduledResults } from "@/lib/utils";

type ScheduledPostTabProps = {
  filter: string;
  resetFilter: () => void;
}

function debounce(func: (id: string) => void, wait: number): (id: string) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return function(id: string) {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(id), wait);
  };
}

const ScheduledPostTab = ({ filter, resetFilter }: ScheduledPostTabProps) => {
  const [endDate, setEndDate] = useState(addDays(new Date(), 30));
  const { scheduledPosts } = useGetScheduledPostV2(format(endDate, 'yyyy-MM-dd'));
  
  const containerRef = useRef<HTMLDivElement | null>(null);
  const initialRef = useRef(true);
  const isScrollingRef = useRef(false);
  
  const [viewId, setViewId] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selected, setSelected] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [scrollLockUntil, setScrollLockUntil] = useState(0);

  useEffect(() => {
    setSelected(viewId);
  }, [viewId]);

  // Create a debounced version of the view change handler
  const debouncedViewChange = useCallback(
    debounce((id: string) => {
      if (initialRef.current) return;
      if (Date.now() < scrollLockUntil) return; 
      
      console.log("Debounced view change:", id);
      setViewId(id);
    }, 300), 
    [scrollLockUntil]
  );

  const viewChangeHandler = (id: string) => {
    if (initialRef.current) return;
    if (Date.now() < scrollLockUntil) return;
    
    console.log("View change triggered:", id);
    debouncedViewChange(id);
  };

  const setChangeTabHandler = (id: string) => {
    // Immediately update the view ID without debounce on click
    setViewId(id);
    
    // Set a lock to prevent scroll events from changing the selection for 1 second
    setScrollLockUntil(Date.now() + 1000);
    
    const container = containerRef.current;
    const targetElement = document.getElementById(id);
    
    if (container && targetElement) {
      isScrollingRef.current = true;
      
      const containerRect = container.getBoundingClientRect();
      const targetRect = targetElement.getBoundingClientRect();
      const scrollAmount = targetRect.top - containerRect.top;

      container.scrollTo({
        top: container.scrollTop + scrollAmount,
        behavior: "smooth",
      });
      
      // Clear the scrolling flag after animation completes
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 500); // Adjust based on your scroll animation duration
    }
  };

  return (
    <div className="size-full">
      <div className="flex flex-col md:flex-row gap-2 items-center justify-between py-4">
        <div className="flex items-center justify-start gap-5 py-3 border-y border-y-gray-200 px-2 rounded-md w-full md:w-9/12 [&::-webkit-scrollbar]:w-1 overflow-x-auto">
          {scheduledPosts && scheduledPosts.data.map((data, index) => (
            <DateTab 
              date={data.date} 
              selected={selected === data.date} 
              setSelected={setChangeTabHandler} 
              key={index} 
            />
          ))}
        </div>
        <div className="w-full md:w-3/12 flex gap-3 items-center justify-center">
          <Button onClick={() => {
            resetFilter()
          }}>Show All Posts</Button>
          <div className="flex flex-row md:flex-col gap-1 md:-mt-3 items-center">
            <p className="text-xs w-full text-center">End Date</p>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  className="flex flex-row md:flex-col gap-2 md:gap-0 h-full items-center max-md:px-4 justify-center rounded-lg w-16 md:aspect-square border border-gray-200"
                >
                  <p className="font-poppins z-10 text-sm">{format(endDate, "MMM")}</p>
                  <span className="relative z-10 font-header font-medium text-base md:text-3xl">{format(endDate, "d")}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={(date: Date | undefined) => {
                    if (date) {
                      setEndDate(date)
                    }
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
      <div id="sections" className="flex flex-col gap-6 max-h-[63vh] overflow-auto" ref={containerRef}>
        {scheduledPosts && filterScheduledResults(scheduledPosts.data, filter).map((data, index) => (
          <DateSection
            key={index}
            date={data.date}
            posts={data.posts}
            position={data.date}
            container={containerRef}
            isInitial={initialRef}
            onInView={viewChangeHandler}
          />
        ))}
      </div>
    </div>
  )
}

export default ScheduledPostTab;
